import { useState } from "react";
import { Shield, KeyRound, Mail, ShieldCheck, Eye, EyeOff, ArrowRight, Lock } from "lucide-react";
import { useToast, useAuth } from "@/rentos/ui";

export default function PlatformAdminSignInPage() {
    const { signIn, verifySignInOtp } = useAuth();
    const { showToast } = useToast();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [otp, setOtp] = useState("");
    const [challengeId, setChallengeId] = useState("");
    const [otpExpiresAt, setOtpExpiresAt] = useState("");
    const [debugOtp, setDebugOtp] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const isOtpStep = Boolean(challengeId);

    function resetOtpStep() {
        setOtp(""); setChallengeId(""); setOtpExpiresAt(""); setDebugOtp("");
    }

    return (
        <div style={{ display: "flex", minHeight: "100vh" }}>

            {/* ── Left: Brand Panel ── */}
            <div style={{
                display: "none",
                width: "50%",
                background: "linear-gradient(145deg, #0f172a 0%, #1e3a8a 60%, #1e40af 100%)",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                padding: "3rem",
                position: "relative",
                overflow: "hidden",
            }} className="signin-left-panel">
                {/* Decorative blobs */}
                <div style={{
                    position: "absolute", top: "-100px", right: "-100px",
                    width: "400px", height: "400px", borderRadius: "50%",
                    background: "radial-gradient(circle, rgba(99,102,241,0.2), transparent)",
                    pointerEvents: "none",
                }} />
                <div style={{
                    position: "absolute", bottom: "-80px", left: "-80px",
                    width: "300px", height: "300px", borderRadius: "50%",
                    background: "radial-gradient(circle, rgba(15,118,110,0.2), transparent)",
                    pointerEvents: "none",
                }} />

                {/* Content */}
                <div style={{ position: "relative", zIndex: 1, textAlign: "center", maxWidth: "360px" }}>
                    {/* Logo */}
                    <div style={{
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        width: "72px", height: "72px", borderRadius: "20px",
                        background: "rgba(255,255,255,0.08)", backdropFilter: "blur(12px)",
                        border: "1px solid rgba(255,255,255,0.12)", marginBottom: "1.5rem",
                    }}>
                        <img src="/logo-icon.svg" alt="RentOS" style={{ width: "40px", height: "40px" }} />
                    </div>

                    <h2 style={{
                        fontSize: "2.25rem", fontWeight: 800, color: "#ffffff",
                        lineHeight: 1.2, marginBottom: "1rem",
                    }}>
                        Manage the entire<br />
                        <span style={{ color: "#a5b4fc" }}>RentOS platform.</span>
                    </h2>

                    <p style={{
                        fontSize: "1rem", color: "rgba(199,210,254,0.65)",
                        lineHeight: 1.6, marginBottom: "2.5rem",
                    }}>
                        Full visibility and control over every business, vehicle, and transaction on the platform.
                    </p>

                    {/* Security note */}
                    <div style={{
                        marginTop: "2rem", display: "flex", alignItems: "flex-start", gap: "10px",
                        background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
                        borderRadius: "14px", padding: "14px 16px", textAlign: "left",
                    }}>
                        <Lock style={{ width: "14px", height: "14px", color: "#a5b4fc", marginTop: "1px", flexShrink: 0 }} />
                        <p style={{ fontSize: "12px", color: "rgba(199,210,254,0.55)", lineHeight: 1.5, margin: 0 }}>
                            Restricted to authorized platform administrators. All sessions are logged and monitored.
                        </p>
                    </div>
                </div>
            </div>

            {/* ── Right: Sign In Form ── */}
            <div style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                padding: "2rem",
                background: "var(--color-background)",
            }}>
                <div style={{ width: "100%", maxWidth: "400px" }}>

                    {/* Mobile logo */}
                    <div className="signin-mobile-logo" style={{
                        display: "flex", alignItems: "center", gap: "10px", marginBottom: "2rem",
                    }}>
                        <img src="/logo-full.svg" alt="RentOS" style={{ height: "36px", width: "auto" }} />
                        <span style={{
                            marginLeft: "8px", fontSize: "11px", fontWeight: 600,
                            background: "#e0e7ff", color: "#3730a3",
                            borderRadius: "6px", padding: "2px 8px",
                        }}>Platform</span>
                    </div>

                    {/* Heading */}
                    <div style={{ marginBottom: "2rem" }}>
                        <div style={{
                            display: "inline-flex", alignItems: "center", gap: "6px",
                            background: "var(--color-muted)", border: "1px solid var(--color-border)",
                            borderRadius: "999px", padding: "4px 12px", marginBottom: "12px",
                        }}>
                            <Shield style={{ width: "11px", height: "11px", color: "var(--color-muted-foreground)" }} />
                            <span style={{ fontSize: "11px", fontWeight: 600, color: "var(--color-muted-foreground)" }}>
                                Admin Access Only
                            </span>
                        </div>
                        <h1 style={{
                            fontSize: "1.875rem", fontWeight: 800,
                            color: "var(--color-foreground)", marginBottom: "0.5rem",
                        }}>
                            {isOtpStep ? "Verify your identity" : "Admin Sign In"}
                        </h1>
                        <p style={{ fontSize: "0.9375rem", color: "var(--color-muted-foreground)" }}>
                            {isOtpStep ? `Enter the 6-digit code sent to ${email}` : "Restricted to authorized administrators"}
                        </p>
                    </div>

                    {/* Form */}
                    <form onSubmit={async (e) => {
                        e.preventDefault();
                        setIsLoading(true);
                        try {
                            if (!isOtpStep) {
                                const result = await signIn(email, password);
                                if (!result.ok) { showToast(result.message, "error"); return; }
                                if (!result.requiresOtp) {
                                    showToast(result.message, "success");
                                    await new Promise(r => setTimeout(r, 50));
                                    window.location.replace(result.redirectTo ?? "/console");
                                    return;
                                }
                                if (!result.challengeId) {
                                    showToast("OTP challenge was not created. Please retry.", "error"); return;
                                }
                                setChallengeId(result.challengeId);
                                setOtpExpiresAt(result.otpExpiresAt ?? "");
                                setDebugOtp(result.debugOtp ?? "");
                                showToast(result.message, "success");
                                return;
                            }
                            if (!otp.trim()) { showToast("Enter the OTP from your email.", "warning"); return; }
                            const result = await verifySignInOtp(challengeId, otp.trim());
                            if (!result.ok) { showToast(result.message, "error"); return; }
                            showToast(result.message, "success");
                            // Small delay to ensure localStorage write from verifySignInOtp
                            // has been flushed before the hard navigation triggers a page reload.
                            await new Promise(r => setTimeout(r, 50));
                            window.location.replace(result.redirectTo ?? "/console");
                        } finally {
                            setIsLoading(false);
                        }
                    }} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>

                        {!isOtpStep ? (
                            <>
                                {/* Email */}
                                <div>
                                    <label style={{
                                        display: "block", fontSize: "11px", fontWeight: 700,
                                        color: "var(--color-muted-foreground)", textTransform: "uppercase",
                                        letterSpacing: "0.08em", marginBottom: "6px",
                                    }}>
                                        Email Address <span style={{ color: "var(--color-error)" }}>*</span>
                                    </label>
                                    <div style={{ position: "relative" }}>
                                        <Mail style={{
                                            position: "absolute", left: "14px", top: "50%",
                                            transform: "translateY(-50%)", width: "16px", height: "16px",
                                            color: "var(--color-muted-foreground)", pointerEvents: "none",
                                        }} />
                                        <input
                                            type="email" value={email} required
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="admin@rentos.tech"
                                            style={{
                                                width: "100%", height: "48px", paddingLeft: "44px", paddingRight: "16px",
                                                border: "1.5px solid var(--color-border)", borderRadius: "12px",
                                                background: "var(--color-card)", fontSize: "14px",
                                                color: "var(--color-foreground)", outline: "none", boxSizing: "border-box",
                                            }}
                                            onFocus={(e) => { e.target.style.borderColor = "#1e3a8a"; e.target.style.boxShadow = "0 0 0 3px rgba(30,58,138,0.1)"; }}
                                            onBlur={(e) => { e.target.style.borderColor = "var(--color-border)"; e.target.style.boxShadow = "none"; }}
                                        />
                                    </div>
                                </div>

                                {/* Password */}
                                <div>
                                    <label style={{
                                        display: "block", fontSize: "11px", fontWeight: 700,
                                        color: "var(--color-muted-foreground)", textTransform: "uppercase",
                                        letterSpacing: "0.08em", marginBottom: "6px",
                                    }}>
                                        Password <span style={{ color: "var(--color-error)" }}>*</span>
                                    </label>
                                    <div style={{ position: "relative" }}>
                                        <KeyRound style={{
                                            position: "absolute", left: "14px", top: "50%",
                                            transform: "translateY(-50%)", width: "16px", height: "16px",
                                            color: "var(--color-muted-foreground)", pointerEvents: "none",
                                        }} />
                                        <input
                                            type={showPassword ? "text" : "password"} value={password} required
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="Enter your password"
                                            style={{
                                                width: "100%", height: "48px", paddingLeft: "44px", paddingRight: "48px",
                                                border: "1.5px solid var(--color-border)", borderRadius: "12px",
                                                background: "var(--color-card)", fontSize: "14px",
                                                color: "var(--color-foreground)", outline: "none", boxSizing: "border-box",
                                            }}
                                            onFocus={(e) => { e.target.style.borderColor = "#1e3a8a"; e.target.style.boxShadow = "0 0 0 3px rgba(30,58,138,0.1)"; }}
                                            onBlur={(e) => { e.target.style.borderColor = "var(--color-border)"; e.target.style.boxShadow = "none"; }}
                                        />
                                        <button type="button" onClick={() => setShowPassword(!showPassword)} style={{
                                            position: "absolute", right: "14px", top: "50%",
                                            transform: "translateY(-50%)", background: "none", border: "none",
                                            cursor: "pointer", color: "var(--color-muted-foreground)", padding: "0",
                                            display: "flex", alignItems: "center",
                                        }}>
                                            {showPassword
                                                ? <EyeOff style={{ width: "16px", height: "16px" }} />
                                                : <Eye style={{ width: "16px", height: "16px" }} />}
                                        </button>
                                    </div>
                                    <div style={{ textAlign: "right", marginTop: "6px" }}>
                                        <a href="/forgot-password" style={{ fontSize: "12px", color: "#4f46e5", textDecoration: "none", fontWeight: 600, display: "inline-block", padding: "8px 0", minHeight: "44px", lineHeight: "28px" }}>Forgot Password?</a>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                {/* OTP */}
                                <div>
                                    <label style={{
                                        display: "block", fontSize: "11px", fontWeight: 700,
                                        color: "var(--color-muted-foreground)", textTransform: "uppercase",
                                        letterSpacing: "0.08em", marginBottom: "6px",
                                    }}>
                                        One-Time Password <span style={{ color: "var(--color-error)" }}>*</span>
                                    </label>
                                    <div style={{ position: "relative" }}>
                                        <ShieldCheck style={{
                                            position: "absolute", left: "14px", top: "50%",
                                            transform: "translateY(-50%)", width: "16px", height: "16px",
                                            color: "var(--color-muted-foreground)", pointerEvents: "none",
                                        }} />
                                        <input
                                            type="text" value={otp} required
                                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                            placeholder="000000"
                                            style={{
                                                width: "100%", height: "48px", paddingLeft: "44px", paddingRight: "16px",
                                                border: "1.5px solid var(--color-border)", borderRadius: "12px",
                                                background: "var(--color-card)", fontSize: "20px", letterSpacing: "0.3em",
                                                color: "var(--color-foreground)", outline: "none", boxSizing: "border-box",
                                            }}
                                            onFocus={(e) => { e.target.style.borderColor = "#1e3a8a"; e.target.style.boxShadow = "0 0 0 3px rgba(30,58,138,0.1)"; }}
                                            onBlur={(e) => { e.target.style.borderColor = "var(--color-border)"; e.target.style.boxShadow = "none"; }}
                                        />
                                    </div>
                                </div>
                                {(otpExpiresAt || debugOtp) && (
                                    <div style={{
                                        background: "var(--color-muted)", border: "1px solid var(--color-border)",
                                        borderRadius: "10px", padding: "10px 14px", fontSize: "12px",
                                        color: "var(--color-muted-foreground)",
                                    }}>
                                        {otpExpiresAt && <div>Expires: {new Date(otpExpiresAt).toLocaleTimeString()}</div>}
                                        {debugOtp && <div style={{ color: "var(--color-warning)", fontFamily: "monospace" }}>Dev OTP: {debugOtp}</div>}
                                    </div>
                                )}
                            </>
                        )}

                        {/* Submit */}
                        <button type="submit" disabled={isLoading} style={{
                            width: "100%", height: "48px", borderRadius: "12px", border: "none",
                            background: "linear-gradient(135deg, #1e3a8a, #1e40af)",
                            color: "#fff", fontSize: "14px", fontWeight: 700,
                            cursor: isLoading ? "not-allowed" : "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                            opacity: isLoading ? 0.7 : 1, transition: "opacity 0.2s", marginTop: "4px",
                        }}>
                            {isLoading ? (
                                <span style={{
                                    width: "18px", height: "18px", borderRadius: "50%",
                                    border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff",
                                    animation: "spin 0.7s linear infinite", display: "inline-block",
                                }} />
                            ) : (
                                <>
                                    {isOtpStep ? "Verify & Sign In" : "Continue"}
                                    <ArrowRight style={{ width: "16px", height: "16px" }} />
                                </>
                            )}
                        </button>

                        {isOtpStep && (
                            <button type="button" onClick={resetOtpStep} style={{
                                width: "100%", height: "44px", borderRadius: "12px",
                                border: "1.5px solid var(--color-border)", background: "transparent",
                                color: "var(--color-foreground)", fontSize: "14px", fontWeight: 600,
                                cursor: "pointer",
                            }}>
                                ← Use a different account
                            </button>
                        )}
                    </form>

                    {/* Dev accounts */}
                    {import.meta.env.MODE === "development" && import.meta.env.VITE_DEV_ADMIN_EMAIL && (
                        <div style={{ marginTop: "2rem", paddingTop: "1.5rem", borderTop: "1px solid var(--color-border)" }}>
                            <p style={{
                                fontSize: "10px", fontWeight: 700, textTransform: "uppercase",
                                letterSpacing: "0.1em", color: "var(--color-muted-foreground)", marginBottom: "8px",
                            }}>Dev Accounts</p>
                            <button type="button"
                                onClick={() => {
                                    setEmail(import.meta.env.VITE_DEV_ADMIN_EMAIL ?? "");
                                    setPassword(import.meta.env.VITE_DEV_ADMIN_PASSWORD ?? "");
                                }}
                                style={{
                                    width: "100%", padding: "8px 12px", borderRadius: "10px",
                                    background: "#0f172a", color: "#f8fafc", border: "none",
                                    fontSize: "13px", fontWeight: 500, cursor: "pointer",
                                    display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                                }}>
                                <Shield style={{ width: "14px", height: "14px" }} /> Super Admin
                            </button>
                        </div>
                    )}
                </div>

                {/* Legal links */}
                <div style={{ marginTop: "1.5rem", textAlign: "center" }}>
                  <p style={{ fontSize: "11px", color: "var(--color-muted-foreground)" }}>
                    By signing in, you agree to our{" "}
                    <a href={(import.meta.env.VITE_LANDING_URL || "https://rentos.tech") + "/legal/terms-of-service"} target="_blank" rel="noopener noreferrer" style={{ color: "#4f46e5", textDecoration: "underline" }}>Terms of Service</a>
                    {" "}and{" "}
                    <a href={(import.meta.env.VITE_LANDING_URL || "https://rentos.tech") + "/legal/privacy-policy"} target="_blank" rel="noopener noreferrer" style={{ color: "#4f46e5", textDecoration: "underline" }}>Privacy Policy</a>.
                  </p>
                </div>
            </div>

            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                @media (min-width: 1024px) {
                    .signin-left-panel { display: flex !important; }
                    .signin-mobile-logo { display: none !important; }
                }
            `}</style>
        </div>
    );
}
